<template>
  <div>
    <div class="d-flex align-center flex-wrap ga-3 mb-4">
      <div class="text-h6">Usage &amp; Cost</div>
      <v-chip size="small" color="grey" variant="tonal">Estimated</v-chip>
      <v-spacer />
      <v-select
        v-model="rangeDays"
        :items="rangeOptions"
        item-title="label"
        item-value="value"
        density="compact"
        variant="outlined"
        hide-details
        style="max-width: 200px"
        @update:modelValue="fetchUsage"
      />
    </div>

    <div class="text-caption text-medium-emphasis mb-4">
      Estimated cost = recorded tokens &times; an internal per-model pricing table — not a provider invoice.
      Tracking began when usage instrumentation was deployed; earlier periods show no data.
    </div>

    <div v-if="loading" class="d-flex justify-center pa-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="rows.length === 0" class="text-center pa-8 text-medium-emphasis">
      <v-icon size="40" class="mb-2 text-disabled">mdi-chart-box-outline</v-icon>
      <div>No AI usage recorded for this organization in the selected range.</div>
    </div>

    <template v-else>
      <!-- KPI cards -->
      <div class="kpi-grid mb-6">
        <v-card variant="outlined" class="pa-4">
          <div class="text-caption text-medium-emphasis">Est. cost</div>
          <div class="text-h5 font-weight-medium">{{ formatCost(totals.cost) }}</div>
          <div class="text-caption text-disabled">estimated · not billed</div>
        </v-card>
        <v-card variant="outlined" class="pa-4">
          <div class="text-caption text-medium-emphasis">AI calls</div>
          <div class="text-h5 font-weight-medium">{{ totals.calls.toLocaleString() }}</div>
        </v-card>
        <v-card variant="outlined" class="pa-4">
          <div class="text-caption text-medium-emphasis">Total tokens</div>
          <div class="text-h5 font-weight-medium">{{ formatTokens(totals.tokens) }}</div>
        </v-card>
        <v-card variant="outlined" class="pa-4">
          <div class="text-caption text-medium-emphasis">Active users</div>
          <div class="text-h5 font-weight-medium">{{ totals.users }}</div>
        </v-card>
      </div>

      <!-- Breakdowns: tokens and est. cost as separate charts, both by model -->
      <div class="breakdown-grid">
        <v-card variant="outlined" class="pa-4">
          <div class="text-subtitle-2 font-weight-bold mb-3">Tokens by model</div>
          <div v-for="row in byModel" :key="row.key" class="bar-row">
            <div class="bar-label text-truncate" :title="modelDisplayName(row.key)">{{ modelDisplayName(row.key) }}</div>
            <div class="bar-track">
              <div class="bar-fill" :style="{ width: barWidth(row.tokens, maxModelTokens) }" />
            </div>
            <div class="bar-value">{{ formatTokens(row.tokens) }}</div>
          </div>
        </v-card>

        <v-card variant="outlined" class="pa-4">
          <div class="text-subtitle-2 font-weight-bold mb-3">Est. cost by model</div>
          <div v-for="row in byModelCost" :key="row.key" class="bar-row">
            <div class="bar-label text-truncate" :title="modelDisplayName(row.key)">{{ modelDisplayName(row.key) }}</div>
            <div class="bar-track">
              <div class="bar-fill bar-fill--alt" :style="{ width: barWidth(row.cost, maxModelCost) }" />
            </div>
            <div class="bar-value">{{ formatCost(row.cost) }}</div>
          </div>
        </v-card>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import client from '@/graphql/client'
import GetOrgUsage from '@/graphql/queries/GetOrgUsage'
import { modelDisplayName } from '@/constants/aiModels'

const props = defineProps({
  orgId: { type: String, required: true }
})

const rangeDays = ref(30)
const rangeOptions = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 }
]

const rows = ref([])
const loading = ref(false)

const computeRange = (days) => {
  const to = new Date()
  to.setHours(0, 0, 0, 0)
  to.setDate(to.getDate() + 1)
  const from = new Date()
  from.setHours(0, 0, 0, 0)
  from.setDate(from.getDate() - days + 1)

  return { from: from.toISOString(), to: to.toISOString() }
}

const fetchUsage = async () => {
  if (!props.orgId) {
    return
  }
  loading.value = true
  try {
    const { from, to } = computeRange(rangeDays.value)
    // Page through ALL usage rows (the resolver returns 1000 per page) so totals
    // are never silently truncated for heavier tenants. Aggregation stays client-side.
    const all = []
    let nextToken = null
    do {
      const res = await client.query({
        operationName: 'getOrgUsage',
        query: GetOrgUsage,
        variables: { orgId: props.orgId, from, to, nextToken }
      })
      const page = res.getOrgUsage || {}
      all.push(...(page.items || []))
      nextToken = page.nextToken
    } while (nextToken)
    rows.value = all
  } catch (e) {
    console.error(e)
    rows.value = []
  }
  loading.value = false
}

onMounted(fetchUsage)

const totals = computed(() => {
  const r = rows.value
  const users = new Set(r.map((x) => x.userId).filter(Boolean))

  return {
    calls: r.length,
    tokens: r.reduce((s, x) => s + (x.totalTokens || 0), 0),
    cost: r.reduce((s, x) => s + (x.estCost || 0), 0),
    users: users.size
  }
})

const groupBy = (key, valueFn) => {
  const map = {}
  for (const row of rows.value) {
    const k = row[key] || 'unknown'
    if (!map[k]) {
      map[k] = { key: k, cost: 0, calls: 0, tokens: 0 }
    }
    map[k].cost += row.estCost || 0
    map[k].calls += 1
    map[k].tokens += row.totalTokens || 0
  }

  return Object.values(map).sort((a, b) => valueFn(b) - valueFn(a))
}

// Tokens chart: models ranked by token volume. Cost chart: same models ranked by cost.
const byModel = computed(() => groupBy('model', (x) => x.tokens))
const byModelCost = computed(() => groupBy('model', (x) => x.cost))

const maxModelTokens = computed(() => Math.max(...byModel.value.map((x) => x.tokens), 1))
const maxModelCost = computed(() => Math.max(...byModelCost.value.map((x) => x.cost), 0.0001))

const barWidth = (value, max) => `${Math.max(2, Math.round((value / max) * 100))}%`

const formatCost = (n) => `$${(n || 0).toFixed(2)}`
const formatTokens = (n) => {
  if (n >= 1e6) {
    return `${(n / 1e6).toFixed(1)}M`
  }
  if (n >= 1e3) {
    return `${(n / 1e3).toFixed(1)}K`
  }

  return String(n)
}
</script>

<style scoped>
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}
.breakdown-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}
.bar-row {
  display: grid;
  grid-template-columns: 130px 1fr 64px;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.bar-label {
  font-size: 13px;
}
.bar-track {
  background: rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 4px;
  height: 10px;
  overflow: hidden;
}
.bar-fill {
  height: 100%;
  background: rgb(var(--v-theme-primary));
  border-radius: 4px;
}
.bar-fill--alt {
  background: rgb(var(--v-theme-secondary));
}
.bar-value {
  font-size: 13px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
</style>
